/* eslint-disable import/prefer-default-export */
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import t from 'react-translate';
import { Result } from 'redux/schemas/api';
import {
  LecturePageMetaItem,
  LecturePageMetaParams,
  LecturePageMetaResponse,
} from 'redux/schemas/api/timelines';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { AwardedPoint, TimelineActivity } from 'redux/schemas/models/activity';
import { LecturePage } from 'redux/schemas/models/lecture-page';
import { getCourseTimeLine } from 'redux/selectors/timeline';
import { RootState } from 'redux/schemas';
import { TimelineItemTypes } from 'redux/schemas/models/course';
import { CompletionCriteriaType } from 'redux/schemas/app/timeline';
import { AppTimelineState } from 'redux/reducers/timeline';
import { AutomaticCompletionProgress } from 'redux/schemas/models/courseFull';
import { LectureComponent } from '../schemas/models/lecture-component';
import { addAlertMessage } from './alert-messages';

export const getTimelineWithCommunications = createAsyncThunk(
  'GET_FULL_COURSE_TIMELINE_WITH_COMMUNICATIONS',
  async (params: { catalogId: string }) => {
    const response = await axios.get(`${params.catalogId}/course_outline/content_and_communications_structure.json`);
    return response.data.result;
  },
);

export type FetchLecturePageActivitiesParams = {
  catalogId: string,
  lecturePageId: number,
};

/**
 * gets all activities(in lecture component form) for the given lecture page
 * activity = any lecture component that a user can interact with(not display-only like RTE, resources, templates)
 * @param params
 * @returns LectureComponent[]
 */
async function fetchLecturePageActivities(params: FetchLecturePageActivitiesParams) {
  const response = await axios.get<Result<LectureComponent[]>>(`${params.catalogId}/lecture_pages/${params.lecturePageId}/activities.json`);
  return response.data.result;
}

export const getLecturePageActivities = createAsyncThunk(
  'LOAD_LECTURE_PAGE_ACTIVITIES',
  fetchLecturePageActivities,
);

/** This has a generic name but is specifically used to get a new version of the current lecture page after a lecture component is modified. This returns
 * many lecture page fields but I think only the status files like isComplete are relevant
 * Note that we don't handle this via a reducer; instead we pass the response into
 * a getLecturePage action */
export const getCourseLecturePageTimeline = createAsyncThunk(
  'LOAD_LECTURE_PAGE_COURSE_OUTLINE',
  async (params: {
    catalogId: string,
    userId: number,
    lecturePageId: number,
  }, thunkAPI) => {
    const response = await axios.get<Result<LecturePage>>(`${params.catalogId}/users/${params.userId}/course_timeline_lecture_page.json?lecture_page_id=${params.lecturePageId}`);

    // // Manually fire a synchronous getLecturePage fulfilled action to load this response
    // // data into Redux in the same manner as when we normally request a lecture page
    // thunkAPI.dispatch(updateLecturePage.fulfilled(response.data.result, thunkAPI.requestId, {
    //   /* This action only uses this lecturepage param to send lp data up to the backend, and not in the reducer */
    //   lecturePage: null,
    //   catalogId: params.catalogId,
    //   lecturePageId: params.lecturePageId.toString(),
    // }));

    return response.data.result;
  },
);

export const deleteLectureSection = createAsyncThunk(
  'DELETE_LECTURE_SECTION',
  async (params: {
    catalogId: string,
    lectureSectionId: number,
    keepContent: boolean,
  }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.delete(`/${params.catalogId}/lecture_sections/${params.lectureSectionId}?keep_content=${params.keepContent}`);
      return response.data.result;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return rejectWithValue(error);
    }
  },
);

export const resetIsTimelineContentLoaded = createAction('RESET_IS_TIMELINE_CONTENT_LOADED');

export const getFullCourseTimelineContent = createAsyncThunk(
  'GET_FULL_COURSE_TIMELINE_CONTENT',
  (params: {
    catalogId: string,
    isFromLhs?: boolean
  }) => axios.get(`/${params.catalogId}/course_outline/content.json`).then((response) => response.data.result),
);

export const getTimelineSectionProgress = createAsyncThunk(
  'GET_TIMELINE_SECTION_PROGRESS',
  (params: {
    sectionId: number,
    catalogId: string,
    vieweeId?: number,
    isEditMode?: boolean
  }) => {
    const apiParams: {
      viewee_id?: number,
      fields?: string,
    } = {};
    if (params.vieweeId) {
      apiParams.viewee_id = params.vieweeId;
    }
    // If it is edit mode, then we can send fields parameter to only get
    // total_points field in the response.
    if (params.isEditMode) {
      apiParams.fields = 'total_points';
    }
    return axios.get(
      `${params.catalogId}/course_outline/progress/${params.sectionId}.json`,
      {
        params: apiParams,
      },
    ).then((response) => response.data.result);
  },
);

export const getTimelineLecturePagesMeta = createAsyncThunk<
  LecturePageMetaResponse,
  LecturePageMetaParams
>(
  'GET_TIMELINE_LECTURE_PAGES_META',
  (params) => axios.get(
    `${params.catalogId}/course_outline/lecture_pages_meta.json`,
    {
      params: {
        lecture_page_ids: JSON.stringify(params.lecturePageIds),
      },
    },
  ).then((response) => response.data.result),
);

export const getTimelineLecturePagesMetaBatched = createAsyncThunk<
  Record<number, LecturePageMetaItem>,
  LecturePageMetaParams
>(
  'GET_TIMELINE_LECTURE_PAGES_META_BATCHED',
  async (params, thunkAPI) => {
    const LECTURE_PAGES_META_BATCH_SIZE = 10;
    const metaByLecturePageId: Record<number, LecturePageMetaItem> = {};

    const batches = params.lecturePageIds.reduce<number[][]>((acc, id, index) => {
      const batchIndex = Math.trunc(index / LECTURE_PAGES_META_BATCH_SIZE);
      acc[batchIndex] = acc[batchIndex] || [];
      acc[batchIndex].push(id);
      return acc;
    }, []);

    await batches.reduce((acc, batch) => acc.then(() => thunkAPI.dispatch(getTimelineLecturePagesMeta({
      catalogId: params.catalogId,
      lecturePageIds: batch,
    })).then((response) => {
      if (!getTimelineLecturePagesMeta.fulfilled.match(response)) {
        throw new Error(response.error.message ?? 'Failed to load lecture page meta');
      }

      response.payload.lecturePages.forEach((metaItem) => { metaByLecturePageId[metaItem.id] = metaItem; });
    })), Promise.resolve());

    return metaByLecturePageId;
  },
);

export const getTimelineSubsectionSummary = createAsyncThunk(
  'GET_TIMELINE_SUBSECTION_SUMMARY',
  async (params: {
    catalogId: string,
    subsectionIds: number[],
    vieweeId?: number,
    isEditMode?: boolean
  }) => {
    const apiParams: {
      lecture_subsections_ids: string,
      viewee_id?: number,
      fields?: string,
    } = {
      lecture_subsections_ids: JSON.stringify(params.subsectionIds),
    };
    if (params.vieweeId) {
      apiParams.viewee_id = params.vieweeId;
    }
    if (params.isEditMode) {
      apiParams.fields = 'total_points';
    }
    return axios.get(
      `${params.catalogId}/course_outline/lecture_subsection_progress.json`,
      {
        params: apiParams,
      },
    ).then((response) => response.data.result);
  },
);

export const getFullCourseTimelineProgress = createAsyncThunk(
  'GET_FULL_COURSE_TIMELINE_PROGRESS',
  async (params: {
    catalogId: string,
    sectionId?: number,
    vieweeId?: number,
    isEditMode?: boolean,
  }, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const timeline = getCourseTimeLine(state);

    const loadSectionData = async (sectionId) => {
      await thunkAPI.dispatch(getTimelineSectionProgress({
        catalogId: params.catalogId,
        sectionId,
        vieweeId: params.vieweeId,
        isEditMode: params.isEditMode,
      }));
    };

    // Load progress of current section if present
    if (params.sectionId) {
      await loadSectionData(params.sectionId);
    }

    const sections = timeline.filter((each) => each.type === TimelineItemTypes.SECTION
        && (params.sectionId ? each.id !== params.sectionId : true));

    // Load progress of rest of sections one by one
    sections.reduce((acc, section) => new Promise((res) => {
      acc.finally(() => {
        loadSectionData(section.id).finally(res);
      });
    }), Promise.resolve());

    const BATCH_SIZE = 10;

    const subsections = timeline.filter((item) => item.type === TimelineItemTypes.SUBSECTION).map((item) => item.id);

    const batches = subsections.reduce<number[][]>((acc, curr, index) => {
      const batchIndex = Math.trunc(index / BATCH_SIZE);

      acc[batchIndex] = acc[batchIndex] || [];

      acc[batchIndex].push(curr);

      return acc;
    }, []);

    // Start loading summaries of all subsections in batches
    await batches.reduce((acc, curr) => new Promise((res) => {
      acc.finally(() => {
        thunkAPI.dispatch(getTimelineSubsectionSummary({
          catalogId: params.catalogId,
          subsectionIds: curr,
          vieweeId: params.vieweeId,
          isEditMode: params.isEditMode,
        })).finally(res);
      });
    }), Promise.resolve());

    if (params.isEditMode) {
      const LECTURE_PAGES_META_BATCH_SIZE = 10;
      const lecturePageIds = timeline
        .filter((item) => item.type === TimelineItemTypes.PAGE)
        .map((item) => item.id);

      const lecturePageBatches = lecturePageIds.reduce<number[][]>((acc, curr, index) => {
        const batchIndex = Math.trunc(index / LECTURE_PAGES_META_BATCH_SIZE);
        acc[batchIndex] = acc[batchIndex] || [];
        acc[batchIndex].push(curr);
        return acc;
      }, []);

      await lecturePageBatches.reduce((acc, curr) => new Promise((res) => {
        acc.finally(() => {
          thunkAPI.dispatch(getTimelineLecturePagesMeta({
            catalogId: params.catalogId,
            lecturePageIds: curr,
          })).finally(res);
        });
      }), Promise.resolve());
    }
  },
);

// This action is used to reset the cached course lookup for a specific catalogId.
export const resetTimelineCachedCourseLookUp = createAction<string>('RESET_TIMELINE_CACHED_COURSE_LOOKUP');
// This action is used to reset ALL the cached course data.
export const resetAllTimelineCachedCourseLookUp = createAction<string>('RESET_ALL_TIMELINE_CACHED_COURSE_LOOKUP');
export const resetTimelineCachedCourseLookUpProgress = createAction<string>('RESET_TIMELINE_CACHED_COURSE_LOOKUP_PROGRESS');

export const updateTimeline = createAsyncThunk(
  'UPDATE_TIMELINE',
  ({
    lecturePageId,
    isEditMode = false,
  }: {
    lecturePageId: number,
    isEditMode?: boolean,
  }, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const catalogId = state.app.currentCatalogId;

    const sectionId = state.models.lecturePages[lecturePageId].lectureSectionId;
    const section = state.models.lectureSections[sectionId];

    if (section.type === 'LectureSubsection') {
      thunkAPI.dispatch(getTimelineSubsectionSummary({
        catalogId,
        subsectionIds: [sectionId],
        isEditMode,
      }));
    }

    thunkAPI.dispatch(getTimelineSectionProgress({
      catalogId,
      sectionId,
      isEditMode,
    }));

    if (isEditMode) {
      thunkAPI.dispatch(getTimelineLecturePagesMeta({
        catalogId,
        lecturePageIds: [lecturePageId],
      }));
    }
  },
);

export const getCurrentLecture = createAsyncThunk(
  'GET_CURRENT_LECTURE',
  async (params: { catalogId: string }) => (
    await axios.get<Result<AppTimelineState['currentLecturePage']>>(`${params.catalogId}/users/current_lecture.json`)
  ).data,
);

export const getCourseCompletionProgress = createAsyncThunk<AutomaticCompletionProgress, { catalogId: string, vieweeId?: number }>(
  'GET_COMPLETION_PROGRESS',
  (params: { catalogId: string, vieweeId?: number }) => axios.get(
    `${params.catalogId}/course_outline/progress_towards_completion.json${params.vieweeId ? `?viewee_id=${params.vieweeId}` : ''}`,
  ).then((response) => response.data.result),
);

export const togglePortableCompletionPanel = createAction('TOGGLE_PORTABLE_COMPLETION_PANEL');

export const resetShowPortableCompletionPanel = createAction('RESET_SHOW_PORTABLE_COMPLETION_PANEL');

export const selectPortableCompletionPanelTab = createAction<CompletionCriteriaType>('SELECT_PORTABLE_COMPLETION_TAB');

export const resetPortableCompletionPanelData = createAction<string>('RESET_PORTABLE_COMPLETION_PANEL_DATA');

// Todos list in timeline has two views, All Todos and Only required ones. Reset to action
// to clean up things when the views are switched
export const resetTimelineTodo = createAction<{ catalogId: string }>('RESET_TIMELINE_TODO');

export const getTimelineTodosList = createAsyncThunk<
{ perPage: number, todos: TimelineActivity[] },
{ catalogId: string, page: number, onlyForCompletion?: boolean, vieweeId?: number }
>(
  'GET_TIMELINE_TODOS_LIST',
  (params: { catalogId: string, page: number, onlyForCompletion: boolean, vieweeId: number }) => axios.get(
    `${params.catalogId}/course_outline/todos.json?page=${params.page}${params.onlyForCompletion ? '&only=required_for_completion' : ''}${params.vieweeId ? `&viewee_id=${params.vieweeId}` : ''}`,
  ).then((response) => response.data.result),
);

export const getTimelineAssignmentsList = createAsyncThunk<
{ perPage: number, todos: TimelineActivity[] },
{ catalogId: string, page: number, vieweeId?: number }
>(
  'GET_TIMELINE_ASSIGNMENTS_LIST',
  (params: { catalogId: string, page: number, vieweeId: number }) => axios.get(
    `${params.catalogId}/course_outline/exercises_progress.json?page=${params.page}${params.vieweeId ? `&viewee_id=${params.vieweeId}` : ''}`,
  ).then((response) => response.data.result),
);

export const getTimelinePointsList = createAsyncThunk<
{ perPage: number, todos: TimelineActivity[] },
{ catalogId: string, page: number, vieweeId?: number }
>(
  'GET_TIMELINE_POINTS_LIST',
  (params: { catalogId: string, page: number, vieweeId: number }) => axios.get(
    `${params.catalogId}/course_outline/received_points.json?page=${params.page}${params.vieweeId ? `&viewee_id=${params.vieweeId}` : ''}`,
  ).then((response) => response.data.result),
);

export const getAwardedPoints = createAsyncThunk<AwardedPoint[], { catalogId: string, vieweeId: number }>(
  'GET_AWARDED_POINTS',
  (params: { catalogId: string, vieweeId: number }) => axios.get(
    `/${params.catalogId}/users/awarded_points.json?viewee_id=${params.vieweeId}`,
  ).then((response) => response.data.result),
);

